import { PaymentDetails, PaymentMethod, VoucherDetails } from '../types';
import { Logger } from './logger';
import { APP_CONFIG } from '../config';
import { apiClient, isExternalBackend } from './apiClient';
import { sendEmail, buildVoucherPurchaseEmail, buildMembershipPaymentEmail } from './emailService';

/**
 * Shared Payment Service
 * Handles core processing logic for payments and vouchers.
 * Routes through external backend API when USE_EXTERNAL_BACKEND is enabled.
 */

const simulateNetworkDelay = (delay = 1000) => new Promise(resolve => setTimeout(resolve, delay));

import { validateCardNumber, validateExpiry, validateCVV } from '../lib/paymentValidation';

/**
 * Common logic for processing success/failure based on payment details
 */
export const validatePaymentDetails = (paymentDetails: PaymentDetails) => {
    if (paymentDetails?.cardName?.toLowerCase() === 'fail') {
        Logger.error('Simulated payment failure (cardName is "fail")');
        throw new Error('Payment failed due to simulated error.');
    }

    if (paymentDetails.method === 'card') {
        if (paymentDetails.cardNumber && !validateCardNumber(paymentDetails.cardNumber)) {
            throw new Error('Invalid card number.');
        }
        if (paymentDetails.expiryDate && !validateExpiry(paymentDetails.expiryDate)) {
            throw new Error('Invalid or expired date.');
        }
        if (paymentDetails.cvv && !validateCVV(paymentDetails.cvv)) {
            throw new Error('Invalid CVV.');
        }
    }
};

/**
 * Generate a standardized transaction ID
 */
export const generateTransactionId = (): string => {
    return `TX-${Date.now().toString().slice(-8)}`;
};

/**
 * Standardized membership payment processing (simulation/mock)
 */
export const processMembershipPaymentCore = async (
    amount: string,
    tierId: string,
    paymentDetails: PaymentDetails,
    paymentMethod: PaymentMethod = 'card'
): Promise<{ success: boolean; transactionId: string }> => {
    Logger.info(`Processing membership payment of ${amount} for ${tierId} via ${paymentMethod}`, { paymentMethod });

    await simulateNetworkDelay(2000);
    validatePaymentDetails(paymentDetails);

    const transactionId = generateTransactionId();
    Logger.info('Membership payment processed successfully.', { transactionId });

    return { success: true, transactionId };
};

/**
 * Standardized voucher purchase processing (simulation/mock)
 */
export const processVoucherPurchaseCore = async (details: VoucherDetails): Promise<{ success: boolean; voucherCode: string }> => {
    Logger.info('Processing voucher purchase core', { recipient: details.recipientEmail });

    await simulateNetworkDelay(1500);
    validatePaymentDetails(details.paymentDetails || {});

    // Generate Voucher Code
    const randomSuffix = Math.random().toString(36).substring(2, 5).toUpperCase();
    const voucherCode = `GIFT-${Date.now().toString().slice(-6)}${randomSuffix}`;

    Logger.info('Voucher purchase successful core', { voucherCode });
    return { success: true, voucherCode };
};

/**
 * UIX logic: Process a membership upgrade payment
 */
export const processMembershipPayment = async (
    amount: string,
    tierId: string,
    paymentDetails: PaymentDetails,
    paymentMethod: PaymentMethod = 'card',
    userEmail?: string,
    userName?: string
): Promise<{ success: boolean; transactionId: string }> => {
    const result = isExternalBackend()
        ? await apiClient.post<{ success: boolean; transactionId: string }>('/api/v1/payments/membership', { amount, tierId, paymentDetails, paymentMethod })
        : await processMembershipPaymentCore(amount, tierId, paymentDetails, paymentMethod);

    if (userEmail && result.success) {
        try {
            const tierName = tierId.charAt(0).toUpperCase() + tierId.slice(1);
            const emailContent = buildMembershipPaymentEmail(userName || 'User', userEmail, tierName, amount, result.transactionId);
            await sendEmail(userEmail, emailContent.subject, emailContent.html, emailContent.text);
            await sendEmail(APP_CONFIG.EMAIL_TO, `[ADMIN] ${emailContent.subject}`, emailContent.html, emailContent.text);
        } catch (e) {
            Logger.error('Failed to send membership confirmation emails', e);
        }
    }
    return result;
};

/**
 * UIX logic: Handle voucher purchase
 */
export const submitVoucherPurchase = async (details: VoucherDetails): Promise<{ success: boolean; voucherCode: string }> => {
    try {
        const result = isExternalBackend()
            ? await apiClient.post<{ success: boolean; voucherCode: string }>('/api/v1/vouchers/purchase', details)
            : await processVoucherPurchaseCore(details);

        const emailContent = buildVoucherPurchaseEmail(details, result.voucherCode);
        await sendEmail(APP_CONFIG.EMAIL_TO, emailContent.subject, emailContent.html, emailContent.text);
        return result;
    } catch (error) {
        Logger.error('Voucher purchase failed', error);
        throw error;
    }
};
